import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import { PageComponents } from "@ext/enterprise-cloud/components/organizationSettings/PageComponents";
import GesCloudOrgSettingsPage from "@ext/enterprise-cloud/types/GesCloudOrgSettingsPage";
import t from "@ext/localization/locale/translate";
import {
	AppSettingsSidebarTabsRenderer,
	type SidebarItem,
} from "@ext/settings/components/AppSettingsSidebarTabsRenderer";
import { Dialog, DialogContent, DialogTitle } from "@ui-kit/Dialog";
import { Divider } from "@ui-kit/Divider";
import { FeatureIcon } from "@ui-kit/Icon";
import {
	Sidebar,
	SidebarContent,
	SidebarGroup,
	SidebarGroupContent,
	SidebarHeader,
	SidebarProvider,
	SidebarRail,
	SidebarSeparator,
} from "@ui-kit/Sidebar";
import { OverflowTooltip } from "@ui-kit/Tooltip";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MainContentBodyHeader } from "../../../enterpriseCommon/components/SettingsMainContext/MainContentBodyHeader";
import { GesCloudApi, type GesCloudSubscription } from "../../GesCloudApi";
import { ScrollContainerProvider } from "./components/ScrollContainerContext";
import { OrgSettingsHeaderProvider } from "./contexts/OrgSettingsHeaderContext";
import {
	OrganizationSettingsNavigationProvider,
	useOrganizationSettingsNavigation,
} from "./OrganizationSettingsNavigationContext";

function MainContent({ isOpen }: { isOpen: boolean }) {
	const { page, tryNavigate, tryCloseModal } = useOrganizationSettingsNavigation();
	const [subscription, setSubscription] = useState<GesCloudSubscription | null>(null);
	const { url: gesCloudUrl } = PageDataContextService.value.conf.enterpriseCloud;
	const gesCloudApi = useMemo(() => new GesCloudApi(gesCloudUrl), [gesCloudUrl]);

	const onOpenChange = useCallback(
		(open: boolean) => {
			if (!open) {
				tryCloseModal();
			}
		},
		[tryCloseModal],
	);

	const refreshSubscription = useCallback(async () => {
		const data = await gesCloudApi.getSubscription();
		setSubscription(data);
	}, [gesCloudApi]);

	const component = PageComponents[page];

	const sidebarItems = useMemo<SidebarItem[]>(
		() => [
			{
				type: "button",
				key: GesCloudOrgSettingsPage.ORGANIZATION,
				icon: "building",
				label: t("enterprise-cloud.org-settings.pages.organization"),
			},
			{
				type: "button",
				key: GesCloudOrgSettingsPage.REPOS,
				icon: "git-branch",
				label: t("enterprise-cloud.org-settings.pages.repos"),
			},
			{
				type: "button",
				key: GesCloudOrgSettingsPage.MEMBERS,
				icon: "users",
				label: t("enterprise-cloud.org-settings.pages.users"),
			},
			{
				type: "button",
				key: "billing",
				icon: "credit-card",
				label: t("enterprise-cloud.org-settings.pages.billing"),
				children: [
					{
						type: "button",
						key: GesCloudOrgSettingsPage.SUBSCRIPTION,
						icon: "credit-card",
						label: t("enterprise-cloud.org-settings.pages.subscription"),
					},
					{
						type: "button",
						key: GesCloudOrgSettingsPage.PAYMENT_METHODS,
						icon: "wallet",
						label: t("enterprise-cloud.org-settings.pages.payment-methods"),
					},
					{
						type: "button",
						key: GesCloudOrgSettingsPage.AI_WALLET,
						icon: "coins",
						label: t("enterprise-cloud.org-settings.pages.ai-wallet"),
					},
					...(subscription?.billingMode === "legal_entity"
						? [
								{
									type: "button" as const,
									key: GesCloudOrgSettingsPage.DOCUMENTS,
									icon: "file-text" as const,
									label: t("enterprise-cloud.org-settings.pages.documents"),
								},
							]
						: []),
				],
			},
			{
				type: "button",
				key: GesCloudOrgSettingsPage.TOKENS,
				icon: "key-round",
				label: t("enterprise-cloud.org-settings.pages.access-tokens"),
			},
		],
		[subscription?.billingMode],
	);

	useEffect(() => {
		const url = new URL(window.location.href);
		url.search = "";
		window.history.replaceState(null, "", url.toString());
	}, []);

	useEffect(() => {
		void refreshSubscription();
	}, [refreshSubscription]);

	const headerRef = useRef<HTMLElement>();

	const scrollContainerRef = useRef<HTMLDivElement>(null);

	return (
		<Dialog onOpenChange={onOpenChange} open={isOpen}>
			<DialogContent showCloseButton={false} size="FS">
				<SidebarProvider className="[&>:first-child]:z-[20] min-h-0 h-full overflow-hidden ![--sidebar-width:17.25rem] [&_ul]:list-none [&_li]:line-height-[unset] [&_li]:mb-0">
					<Sidebar collapsible="offcanvas">
						<DialogTitle asChild className="-font-sans">
							<SidebarHeader className="px-4 py-3 h-[3.75rem] items-center flex-row">
								<FeatureIcon className="flex-shrink-0" icon="settings" size="sm" />
								<OverflowTooltip className="inline-block max-w-full truncate text-base font-medium">
									{t("enterprise-cloud.org-settings.title")}
								</OverflowTooltip>
							</SidebarHeader>
						</DialogTitle>
						<Divider />
						<SidebarContent className="min-h-0 p-4 [&_ul]:list-none">
							<SidebarGroup className="p-0 font-normal">
								<SidebarGroupContent>
									<AppSettingsSidebarTabsRenderer
										active={page}
										items={sidebarItems}
										onChange={(key) => tryNavigate(key as GesCloudOrgSettingsPage)}
									/>
								</SidebarGroupContent>
							</SidebarGroup>
						</SidebarContent>
						<SidebarSeparator />
						<SidebarRail />
					</Sidebar>
					<main className="flex flex-col w-full max-w-full overflow-y-auto" ref={scrollContainerRef}>
						<OrgSettingsHeaderProvider value={{ headerRef }}>
							<MainContentBodyHeader headerRef={headerRef} />
							<div className="flex-1">
								<ScrollContainerProvider container={scrollContainerRef.current}>
									{component ? component({ subscription, refreshSubscription }) : null}
								</ScrollContainerProvider>
							</div>
						</OrgSettingsHeaderProvider>
					</main>
				</SidebarProvider>
			</DialogContent>
		</Dialog>
	);
}

export const GesCloudOrganizationSettingsModal = ({ onClose }: { onClose: () => void }) => {
	const [isOpen, setIsOpen] = useState(true);

	const closeHandler = useCallback(() => {
		setIsOpen(false);
		onClose();
	}, [onClose]);

	return (
		<OrganizationSettingsNavigationProvider onClose={closeHandler}>
			<MainContent isOpen={isOpen} />
		</OrganizationSettingsNavigationProvider>
	);
};
